import { useAuthStore } from '@common/auth-layer/store/auth.store'

import { isWelcomeMemberOnboardingActive } from './welcome-onboarding-visit.utils'
import type { MemberOnboardingResponse, WelcomeAutoActivityTaskType } from '../types/types'

/** Shared auth user id for session-memory keys (completion / like-comment / query ticks). */
export function welcomeOnboardingMemoryUserId(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim()
  try {
    return String(useAuthStore().userId || '').trim()
  } catch {
    return ''
  }
}

function withCompletedTasks(current: MemberOnboardingResponse, tasks: MemberOnboardingResponse['tasks']): MemberOnboardingResponse {
  const checklistComplete = tasks.length > 0 && tasks.every(task => task.completed)
  return { ...current, tasks, checklistComplete }
}

/**
 * Marks a member checklist step completed in the TanStack cache.
 * Used so the learner board ticks immediately, and again after GET if the
 * server still returns the pre-complete snapshot (POST/GET race).
 */
export function markMemberOnboardingStepComplete(
  current: MemberOnboardingResponse | undefined,
  stepId: string
): MemberOnboardingResponse | undefined {
  if (!current?.tasks?.length || !stepId) return current

  let changed = false
  const tasks = current.tasks.map(task => {
    if (task.id !== stepId || task.completed) return task
    changed = true
    return {
      ...task,
      completed: true,
      completedAt: task.completedAt || new Date().toISOString(),
    }
  })

  if (!changed) return current

  return withCompletedTasks(current, tasks)
}

/**
 * Optimistically ticks incomplete AUTO checklist steps of the given types.
 * Used after like/comment/post mutations while BE owns the real completion.
 */
export function markMemberOnboardingTasksCompleteByTypes(
  current: MemberOnboardingResponse | undefined,
  types: readonly WelcomeAutoActivityTaskType[]
): MemberOnboardingResponse | undefined {
  if (!current?.tasks?.length || !types.length) return current

  const typeSet = new Set<string>(types)
  let changed = false
  const tasks = current.tasks.map(task => {
    if (task.completed || task.trackingMode !== 'AUTO' || !typeSet.has(task.type)) return task
    changed = true
    return {
      ...task,
      completed: true,
      completedAt: task.completedAt || new Date().toISOString(),
    }
  })

  if (!changed) return current

  return withCompletedTasks(current, tasks)
}

/** Session memory of steps the FE already know are done (survives focus/tab refetches while BE lags). */
const rememberedByBoard = new Map<string, Set<string>>()

function boardKey(locationId: string, groupId: string, userId: string): string {
  return `${locationId}:${groupId}:${userId}`
}

function hasMemoryScope(locationId: string | undefined, groupId: string | undefined, userId: string | undefined): boolean {
  return Boolean(locationId && groupId && userId)
}

export function rememberWelcomeCompletedStepIds(
  locationId: string | undefined,
  groupId: string | undefined,
  stepIds: readonly string[],
  userId?: string
): void {
  if (!hasMemoryScope(locationId, groupId, userId) || !stepIds.length) return
  const key = boardKey(locationId as string, groupId as string, userId as string)
  let set = rememberedByBoard.get(key)
  if (!set) {
    set = new Set()
    rememberedByBoard.set(key, set)
  }
  for (const id of stepIds) {
    if (id) set.add(id)
  }
}

export function getRememberedWelcomeCompletedStepIds(
  locationId: string | undefined,
  groupId: string | undefined,
  userId?: string
): string[] {
  if (!hasMemoryScope(locationId, groupId, userId)) return []
  const set = rememberedByBoard.get(boardKey(locationId as string, groupId as string, userId as string))
  return set ? [...set] : []
}

/** Drop session ticks for this board (BE history-sync / ineligible GET). */
export function clearRememberedWelcomeCompletions(locationId: string | undefined, groupId: string | undefined, userId?: string): void {
  if (!hasMemoryScope(locationId, groupId, userId)) return
  rememberedByBoard.delete(boardKey(locationId as string, groupId as string, userId as string))
}

/** Re-apply session-known completions onto a board snapshot (after GET / focus refetch). */
export function applyRememberedWelcomeCompletions(
  current: MemberOnboardingResponse | undefined,
  locationId: string | undefined,
  groupId: string | undefined,
  userId?: string
): MemberOnboardingResponse | undefined {
  if (!current || !hasMemoryScope(locationId, groupId, userId)) return current

  // History-sync / expired window: do not resurrect optimistic ticks after BE closes the board.
  if (!isWelcomeMemberOnboardingActive(current)) {
    clearRememberedWelcomeCompletions(locationId, groupId, userId)
    return current
  }

  const ids = getRememberedWelcomeCompletedStepIds(locationId, groupId, userId)
  if (!ids.length) return current

  let next: MemberOnboardingResponse | undefined = current
  for (const id of ids) {
    next = markMemberOnboardingStepComplete(next, id)
  }
  return next
}

/** Record which tasks of the given types are completed in the current snapshot. */
export function rememberCompletedStepsMatchingTypes(
  current: MemberOnboardingResponse | undefined,
  locationId: string | undefined,
  groupId: string | undefined,
  types: readonly string[],
  userId?: string
): void {
  if (!current?.tasks?.length || !hasMemoryScope(locationId, groupId, userId) || !types.length) return
  const typeSet = new Set(types)
  const ids = current.tasks.filter(task => typeSet.has(task.type) && task.completed).map(task => task.id)
  rememberWelcomeCompletedStepIds(locationId, groupId, ids, userId)
}

/** Test-only reset. */
export function clearRememberedWelcomeCompletionsForTests(): void {
  rememberedByBoard.clear()
}
